import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { CategorySelector } from "@/components/CategorySelector";
import { LeaderboardItem, type LeaderboardEntry } from "@/components/LeaderboardItem";
import { Input } from "@/components/ui/input";
import { Search, Trophy, Clock, Filter } from "lucide-react";

// Sample data
const leaderboardData: LeaderboardEntry[] = [
  { id: "1", rank: 1, name: "WritingWizard", score: 98, date: "2023-05-15", category: "copywriting" },
  { id: "2", rank: 2, name: "WordSmith42", score: 95, date: "2023-05-15", category: "copywriting" },
  { id: "3", rank: 3, name: "ProseMaster", score: 91, date: "2023-05-14", category: "copywriting" },
  { id: "4", rank: 4, name: "CreativeGenius", score: 88, date: "2023-05-14", category: "copywriting" },
  { id: "5", rank: 5, name: "InkSlinger", score: 85, date: "2023-05-13", category: "copywriting" },
  { id: "6", rank: 6, name: "NarrativeNinja", score: 82, date: "2023-05-13", category: "copywriting" },
  { id: "7", rank: 7, name: "StoryWeaver", score: 79, date: "2023-05-12", category: "copywriting" },
  { id: "8", rank: 8, name: "TextCrafter", score: 76, date: "2023-05-12", category: "copywriting" },
  { id: "9", rank: 9, name: "LiteraryLion", score: 74, date: "2023-05-11", category: "copywriting" },
  { id: "10", rank: 10, name: "VerseMaster", score: 72, date: "2023-05-11", category: "copywriting" },
  
  { id: "11", rank: 1, name: "OpinionLeader", score: 97, date: "2023-05-15", category: "opinion" },
  { id: "12", rank: 2, name: "ThoughtfulWriter", score: 94, date: "2023-05-15", category: "opinion" },
  { id: "13", rank: 3, name: "MindfulScribe", score: 90, date: "2023-05-14", category: "opinion" },
  
  { id: "14", rank: 1, name: "RhymeTime", score: 99, date: "2023-05-15", category: "poem" },
  { id: "15", rank: 2, name: "VerseVoyager", score: 96, date: "2023-05-15", category: "poem" },
  { id: "16", rank: 3, name: "SonnetSage", score: 93, date: "2023-05-14", category: "poem" },
];

export default function LeaderboardPage() {
  const [selectedCategory, setSelectedCategory] = useState("copywriting");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredEntries, setFilteredEntries] = useState<LeaderboardEntry[]>([]);
  
  useEffect(() => {
    // Filter entries based on category and search term
    const filtered = leaderboardData
      .filter(entry => entry.category === selectedCategory)
      .filter(entry => entry.name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    setFilteredEntries(filtered);
  }, [selectedCategory, searchTerm]);

  return (
    <div className="min-h-screen pb-16">
      <Navbar />
      
      <main className="container max-w-5xl pt-28 px-4">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-3xl font-bold font-heading flex items-center">
            <Trophy className="text-primary mr-3 h-7 w-7" />
            Leaderboard
          </h1>
          
          <CategorySelector
            selectedCategory={selectedCategory}
            onChange={setSelectedCategory}
            className="w-full sm:w-[200px]"
          />
        </div>
        
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50 h-5 w-5" />
            <Input 
              placeholder="Search writers..." 
              className="pl-10 bg-card/50 border-white/10 focus:border-primary"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        <div className="w-full">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-medium text-primary">All Time Rankings</h3>
            
            <Button variant="outline" size="sm" className="border-white/20 text-white">
              <Filter className="mr-2 h-4 w-4" />
              Filters
            </Button>
          </div>
          
          <div className="mt-0 space-y-2">
            {filteredEntries.length === 0 ? (
              <div className="glass-card text-center py-12">
                <p className="text-white/70">No entries found for this category.</p>
              </div>
            ) : (
              <>
                <div className="space-y-4 mb-6">
                  {filteredEntries.slice(0, 3).map((entry) => (
                    <LeaderboardItem key={entry.id} entry={entry} isTop3={true} />
                  ))}
                </div>
                
                <div className="glass-card rounded-lg overflow-hidden">
                  <div className="p-4 bg-card/80 border-b border-white/10 flex justify-between">
                    <h3 className="font-medium">All Entries</h3>
                    <span className="text-white/60 text-sm">{filteredEntries.length} writers</span>
                  </div>
                  
                  <div className="divide-y divide-white/5">
                    {filteredEntries.map((entry) => (
                      <div key={entry.id} className="p-2">
                        <LeaderboardItem entry={entry} />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        
        <div className="mt-8 text-center">
          <p className="text-white/60 mb-4">Ready to join the competition?</p>
          <Button 
            asChild
            className="bg-gradient-to-r from-primary to-secondary text-white"
          >
            <Link to="/prompt">
              Start Writing
            </Link>
          </Button>
        </div>
      </main>
    </div>
  );
}
